import { useEffect, useState, useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchIcon from "@mui/icons-material/Search";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";
import authUtils from "../../utils/authUtils";
import Loading from "../common/Loading";
import Sidebar from "../common/Sidebar";
import { setUser } from "../../redux/features/userSlice";

const AppLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [globalQuery, setGlobalQuery] = useState("");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  useEffect(() => {
    const checkAuth = async () => {
      const authenticatedUser = await authUtils.isAuthenricated();
      if (!authenticatedUser) {
        navigate("/");
      } else {
        dispatch(setUser(authenticatedUser));
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, dispatch]);

  useEffect(() => {
    if (isDesktop) {
      setMobileNavOpen(false);
    }
  }, [isDesktop]);

  const handleQuickSearch = (event) => {
    event.preventDefault();
    const trimmed = globalQuery.trim();
    if (trimmed) {
      navigate(`/foods?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate("/foods");
    }
  };

  const quickActions = useMemo(
    () => [
      {
        label: "Dashboard",
        href: "/app/dashboard",
        icon: <AnalyticsOutlinedIcon fontSize="small" />,
      },
      {
        label: "Boards",
        href: "/app/boards",
        icon: <ListOutlinedIcon fontSize="small" />,
      },
      {
        label: "Foods",
        href: "/foods",
        icon: <LocalDiningOutlinedIcon fontSize="small" />,
      },
    ],
    []
  );

  const shellGradient =
    "radial-gradient(circle at 10% 20%, rgba(59,130,246,0.08), transparent 50%), radial-gradient(circle at 90% 10%, rgba(16,185,129,0.08), transparent 45%)";

  return loading ? (
    <Loading fullHeight />
  ) : (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#0f172a",
        backgroundImage: shellGradient,
      }}
    >
      <Sidebar
        variant={isDesktop ? "permanent" : "temporary"}
        open={isDesktop ? true : mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <Box
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          background: "linear-gradient(180deg,#0f172a 0%,#111827 65%,#0f172a 100%)",
        }}
      >
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(14px)",
            background:
              "linear-gradient(120deg, rgba(15,23,42,0.9), rgba(15,23,42,0.65))",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              px: { xs: 2, md: 4 },
              py: 2,
              minHeight: 72,
              flexWrap: "wrap",
              rowGap: 1.5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                flexWrap: "wrap",
                rowGap: 1.5,
              }}
            >
              {!isDesktop && (
                <IconButton
                  onClick={() => setMobileNavOpen(true)}
                  sx={{ color: "white" }}
                  aria-label="Open navigation"
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  component="img"
                  src={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"}
                  alt="CaloriesPal logo"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    objectFit: "contain",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    padding: 0.5,
                    boxShadow: "0 10px 30px rgba(15,23,42,0.4)",
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(248,250,252,0.8)" }}
                  >
                    CaloriesPal
                  </Typography>
                  <Typography variant="h6" color="#fff" fontWeight={700}>
                    Creator Hub
                  </Typography>
                </Box>
              </Stack>

              <Box
                component="form"
                onSubmit={handleQuickSearch}
                sx={{
                  flexGrow: 1,
                  minWidth: { xs: "100%", sm: 260 },
                  maxWidth: { xs: "100%", sm: 460 },
                }}
              >
                <OutlinedInput
                  value={globalQuery}
                  onChange={(e) => setGlobalQuery(e.target.value)}
                  placeholder="Search foods or ingredients..."
                  fullWidth
                  size="small"
                  startAdornment={
                    <InputAdornment position="start" sx={{ color: "#94a3b8" }}>
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  }
                  sx={{
                    backgroundColor: "rgba(15,23,42,0.6)",
                    borderRadius: 999,
                    color: "white",
                    "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
                  }}
                />
              </Box>
            </Stack>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1}>
                {quickActions.map((action) => (
                  <Button
                    key={action.label}
                    onClick={() => navigate(action.href)}
                    startIcon={action.icon}
                    size="small"
                    sx={{
                      textTransform: "none",
                      color: "white",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.2)",
                      px: 2,
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.4)",
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 4 },
            background: "linear-gradient(180deg,#f8fafc 0%,#eef2ff 80%)",
          }}
        >
          <Box
            sx={{
              maxWidth: "1600px",
              mx: "auto",
              width: "100%",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
